import { Link } from 'react-router-dom';
import { Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { PortfolioRiskItem } from '@/hooks/usePortfolioRisk';

// ─── Types ──────────────────────────────────────────────────────────────────

interface OverdueReviewWidgetProps {
  count: number;
  items: PortfolioRiskItem[];
  loading?: boolean;
}

// ─── Skeleton ───────────────────────────────────────────────────────────────

function WidgetSkeleton() {
  return (
    <div role="status" aria-label="Loading overdue review data" className="space-y-2">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="h-10 animate-pulse rounded bg-muted" aria-hidden="true" />
      ))}
    </div>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * Overdue Review Items widget — FR-086 Widget 1.
 * Shows jobs with review_status = pending_review AND aging > 48h.
 * Red accent, links to Needs Review tab.
 */
export function OverdueReviewWidget({ count, items, loading }: OverdueReviewWidgetProps) {
  return (
    <section
      className="rounded-lg border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950 p-4 space-y-3"
      aria-label="Overdue review items"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-red-600 dark:text-red-400" aria-hidden="true" />
          <h3 className="text-sm font-semibold text-red-900 dark:text-red-100">Overdue Reviews</h3>
        </div>
        <span
          className="rounded-full bg-red-100 dark:bg-red-900 px-2 py-0.5 text-xs font-bold text-red-700 dark:text-red-300"
          aria-live="polite"
          aria-label={`${count} overdue review items`}
        >
          {count}
        </span>
      </div>

      {loading ? (
        <WidgetSkeleton />
      ) : items.length === 0 ? (
        <p className="text-xs text-red-700 dark:text-red-300 italic">No overdue items.</p>
      ) : (
        <ul className="space-y-1.5" role="list" aria-label="Oldest overdue contracts">
          {items.slice(0, 5).map((item) => (
            <li key={item.id} className="flex items-center justify-between gap-2">
              <Link
                to={`/jobs/${item.id}`}
                className={cn(
                  'truncate text-xs text-red-800 dark:text-red-200 underline hover:no-underline',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded',
                )}
                title={item.file_name}
              >
                {item.file_name}
              </Link>
              <span className="shrink-0 text-xs text-red-600 dark:text-red-400 font-medium">
                {item.aging_hours >= 24
                  ? `${Math.floor(item.aging_hours / 24)}d`
                  : `${Math.round(item.aging_hours)}h`}
              </span>
            </li>
          ))}
        </ul>
      )}

      <Link
        to="/dashboard?tab=needs-review&filter=overdue"
        className="block text-xs font-medium text-red-700 dark:text-red-300 underline hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
      >
        View all overdue items
      </Link>
    </section>
  );
}
